// This service is a thin wrapper around the Fetch API, providing some common functionality like:
// - Base URL from environment variable
// - JSON parsing and content-type handling
// - Error handling with custom HttpError class
// - Automatic inclusion of Authorization header if token is present in environment store
import { useEnvironmentStore } from '@/stores/environmentStore'

const apiUrl = import.meta.env.VITE_API_URL

export class HttpError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

function buildHeaders(init?: HeadersInit) {
  const env = useEnvironmentStore()
  const token = env.token
  const headers = new Headers(init || {})
  headers.set('Content-Type', 'application/json')
  headers.set('X-Client-Type', 'app')

  if (token && token.trim().length > 0) {
    headers.set('Authorization', `Bearer ${token}`)
  } else {
    headers.delete('Authorization')
  }
  return headers
}

async function handle<T>(res: Response): Promise<T> {
  if (res.status === 401 || res.status === 403) {
    useEnvironmentStore().markInvalid()
    const msg = (await res.text().catch(() => '')) || (res.status === 401 ? 'Unauthorized' : 'Forbidden')
    throw new HttpError(res.status, msg)
  }

  if (!res.ok) {
    const msg = (await res.text().catch(() => '')) || res.statusText
    throw new HttpError(res.status, msg)
  }

  const ct = res.headers.get('content-type') || ''
  if (!ct || res.status === 204) return null as T

  return ct.includes('application/json')
    ? ((await res.json()) as T)
    : ((await res.text()) as unknown as T)
}

async function get<T>(path: string) {
  let headers = buildHeaders()
  return handle<T>(await fetch(apiUrl + path, { method: 'GET', headers: headers }))
}

async function post<T>(path: string, body?: any) {
  return handle<T>(
    await fetch(apiUrl + path, {
      method: 'POST',
      headers: buildHeaders(),
      body: body != undefined ? JSON.stringify(body) : undefined
    })
  )
}

async function put<T>(path: string, body?: any) {
  return handle<T>(
    await fetch(apiUrl + path, {
      method: 'PUT',
      headers: buildHeaders(),
      body: body != undefined ? JSON.stringify(body) : undefined
    })
  )
}

export default { get, post, put }
